import type { OnChangeFn, PaginationState, SortingState } from '@tanstack/react-table'
import { CircleAlert } from 'lucide-react'
import { useState } from 'react'

import type { CollectionLine } from '@/api/reports-types'
import { DataTable, dataTableColumns, type DataTableColumn } from '@/components/data-table'
import { MoneyDisplay } from '@/components/money-display'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { formatShortDate } from '@/lib/datetime'

import { ReportExportButtons } from './report-export-buttons'
import { resolveBranchLabel, startOfMonth, today } from './constants'
import { BranchFilterField, LoanOfficerFilterField } from './filters'
import { REPORTS_PERMISSIONS, useReportsPermissions } from './permissions'
import { useCollectionsRegister, useReportsBranchReach } from './queries'
import { ReportHeaderNotice } from './report-header-notice'
import { RestrictedPanel } from './restricted-panel'

const DEFAULT_PAGE_SIZE = 50
const UNSORTED: SortingState = []
const ignoreSortingChange: OnChangeFn<SortingState> = () => undefined

/**
 * The collections register.
 *
 * **Known gap, stated plainly rather than left for a reader to work out from a total that looks a
 * little short:** the endpoint counts `Repayment` and `EarlySettlement` transactions only. An
 * instalment settled by drawing on a called savings guarantee posts as `SecurityDepositOffset`,
 * which this register does not count as collected — it is server behaviour, not something this
 * screen or any of its exports can add back in. See `reports-api.ts`'s own note on the same thing.
 */
export function CollectionsRegisterSection() {
  const permissions = useReportsPermissions()
  const reach = useReportsBranchReach()

  const [fromDate, setFromDate] = useState(() => startOfMonth(today()))
  const [toDate, setToDate] = useState(today)
  const [branchId, setBranchId] = useState<string | null>(null)
  const [loanOfficerId, setLoanOfficerId] = useState<string | null>(null)
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE)

  const query = useCollectionsRegister(
    { fromDate, toDate, branchId, loanOfficerId, page, pageSize },
    permissions.canViewCollections && fromDate !== '' && toDate !== '',
  )

  if (!permissions.canViewCollections) {
    return <RestrictedPanel what="The collections register" permission={REPORTS_PERMISSIONS.collectionsView} />
  }

  const onPaginationChange: OnChangeFn<PaginationState> = (updater) => {
    const current: PaginationState = { pageIndex: page - 1, pageSize }
    const next = typeof updater === 'function' ? updater(current) : updater
    setPage(next.pageIndex + 1)
    setPageSize(next.pageSize)
  }

  const helper = dataTableColumns<CollectionLine>()
  const columns: DataTableColumn<CollectionLine>[] = helper.columns([
    helper.accessor('transactionNumber', {
      header: 'Transaction',
      enableSorting: false,
      cell: (context) => (
        <span className="font-mono text-xs">{context.row.original.transactionNumber}</span>
      ),
      meta: { noWrap: true, widthClass: 'w-36', card: 'lead' },
    }),
    helper.accessor('customerName', {
      header: 'Customer',
      enableSorting: false,
      cell: (context) => (
        <span className="font-medium text-heading">{context.row.original.customerName}</span>
      ),
      meta: { card: 'title' },
    }),
    helper.accessor('groupName', {
      header: 'Group',
      enableSorting: false,
      cell: (context) => context.row.original.groupName ?? <span className="text-muted">Individual</span>,
      meta: { widthClass: 'w-36' },
    }),
    helper.accessor('branchId', {
      header: 'Branch',
      enableSorting: false,
      cell: (context) => (
        <span className="text-xs">{resolveBranchLabel(reach.branches, context.row.original.branchId)}</span>
      ),
      meta: { noWrap: true, widthClass: 'w-28' },
    }),
    helper.accessor('valueDate', {
      header: 'Value date',
      enableSorting: false,
      cell: (context) => (
        <span className="numeric text-xs tabular-nums">
          {formatShortDate(context.row.original.valueDate)}
        </span>
      ),
      meta: { noWrap: true, widthClass: 'w-28' },
    }),
    helper.accessor('amount', {
      header: 'Collected',
      enableSorting: false,
      cell: (context) => (
        <MoneyDisplay
          value={context.row.original.amount.minor}
          currency={context.row.original.amount.currency}
          emphasis="strong"
        />
      ),
      meta: { align: 'right', widthClass: 'w-32', cardLabel: 'Collected' },
    }),
    helper.accessor('principal', {
      header: 'Principal',
      enableSorting: false,
      cell: (context) => (
        <MoneyDisplay
          value={context.row.original.principal.minor}
          currency={context.row.original.principal.currency}
          symbol={false}
        />
      ),
      meta: { align: 'right', widthClass: 'w-28', cardLabel: 'Principal' },
    }),
    helper.accessor('interest', {
      header: 'Interest',
      enableSorting: false,
      cell: (context) => (
        <MoneyDisplay
          value={context.row.original.interest.minor}
          currency={context.row.original.interest.currency}
          symbol={false}
        />
      ),
      meta: { align: 'right', widthClass: 'w-28', cardLabel: 'Interest' },
    }),
    helper.accessor('channel', {
      header: 'Channel',
      enableSorting: false,
      // A call-in reads differently from a place money was taken: no paper reconciles it, and the
      // amber is what stops a scanning eye summing it in with the cash rows above it.
      cell: (context) =>
        context.row.original.isGuaranteeCallIn ? (
          <Badge tone="warning">{context.row.original.channel}</Badge>
        ) : (
          <span className="text-xs">{context.row.original.channel}</span>
        ),
      meta: { widthClass: 'w-24' },
    }),
    helper.accessor('isAllocationBalanced', {
      header: 'Allocation',
      enableSorting: false,
      cell: (context) =>
        context.row.original.isAllocationBalanced ? (
          <span className="text-2xs text-muted">Balanced</span>
        ) : (
          <Badge tone="warning">Unbalanced</Badge>
        ),
      meta: { widthClass: 'w-28' },
    }),
  ])

  return (
    <div className="space-y-4">
      <Alert tone="warning">
        <CircleAlert aria-hidden />
        <div className="space-y-1">
          <AlertTitle>This register understates guarantee call-ins</AlertTitle>
          <AlertDescription>
            It counts Repayment and EarlySettlement transactions only. An instalment settled from a
            called savings guarantee posts as SecurityDepositOffset and does not appear here or in
            the totals below — that is the endpoint's own behaviour, not a filter this screen applies.
          </AlertDescription>
        </div>
      </Alert>

      <Card>
        <CardContent className="grid gap-3 p-4 lg:grid-cols-4">
          <div className="space-y-1.5">
            <Label htmlFor="collections-from">From</Label>
            <Input
              id="collections-from"
              type="date"
              value={fromDate}
              onChange={(event) => {
                setFromDate(event.target.value)
                setPage(1)
              }}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="collections-to">To</Label>
            <Input
              id="collections-to"
              type="date"
              value={toDate}
              onChange={(event) => {
                setToDate(event.target.value)
                setPage(1)
              }}
            />
          </div>
          <BranchFilterField
            id="collections-branch"
            branchId={branchId}
            onChange={(next) => {
              setBranchId(next)
              setLoanOfficerId(null)
              setPage(1)
            }}
          />
          <LoanOfficerFilterField
            id="collections-officer"
            branchId={branchId}
            loanOfficerId={loanOfficerId}
            onChange={(next) => {
              setLoanOfficerId(next)
              setPage(1)
            }}
          />
        </CardContent>
      </Card>

      {query.data ? <ReportHeaderNotice header={query.data.header} /> : null}

      <ReportExportButtons
        query={{
          report: 'CollectionsRegister',
          fromDate,
          toDate,
          branchId,
          loanOfficerId,
          page,
          pageSize,
        }}
        totalRows={query.data?.paging.totalRows}
      />

      {query.data ? (
        <Card>
          <CardHeader>
            <CardTitle>Totals for this range</CardTitle>
            {query.data.unbalancedRowCount > 0 ? (
              <Badge tone="warning">{query.data.unbalancedRowCount} unbalanced rows</Badge>
            ) : null}
          </CardHeader>
          <CardContent className="space-y-3 p-4">
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
              <TotalTile label="Collected (cash)" money={query.data.totalCollected} strong />
              <TotalTile label="Principal" money={query.data.totalPrincipal} />
              <TotalTile label="Interest" money={query.data.totalInterest} />
              <TotalTile label="Penalty" money={query.data.totalPenalty} />
              <TotalTile label="Fees" money={query.data.totalFees} />
            </div>
            {query.data.guaranteeCallInCount > 0 ? (
              <div className="grid gap-3 border-t border-border-subtle pt-3 sm:grid-cols-2 lg:grid-cols-5">
                <TotalTile label="Settled from guarantees" money={query.data.totalGuaranteeCallIns} />
                <p className="text-2xs text-muted sm:col-span-1 lg:col-span-4">
                  {query.data.guaranteeCallInCount} instalment
                  {query.data.guaranteeCallInCount === 1 ? '' : 's'} cleared out of called savings
                  guarantees. Listed on the register, never counted as collected — nobody handed
                  this money to anyone, and a collection rate that included it would flatter exactly
                  the group whose guarantees were called.
                </p>
              </div>
            ) : null}
          </CardContent>
        </Card>
      ) : null}

      <DataTable
        columns={columns}
        data={query.data?.rows}
        rowCount={query.data?.paging.totalRows}
        pagination={{ pageIndex: page - 1, pageSize }}
        onPaginationChange={onPaginationChange}
        sorting={UNSORTED}
        onSortingChange={ignoreSortingChange}
        isLoading={query.isPending}
        isFetching={query.isFetching}
        error={query.isError ? query.error : null}
        onRetry={() => void query.refetch()}
        getRowId={(row) => row.transactionId}
        emptyTitle="Nothing collected"
        emptyDescription="No repayment or early settlement in this range matches the filters."
      />
    </div>
  )
}

function TotalTile({
  label,
  money,
  strong = false,
}: {
  label: string
  money: { minor: number; currency: string }
  strong?: boolean
}) {
  return (
    <div className="rounded-control border border-border-subtle bg-surface p-3">
      <p className="text-2xs text-muted">{label}</p>
      <MoneyDisplay
        value={money.minor}
        currency={money.currency}
        emphasis={strong ? 'strong' : 'normal'}
        className="text-left"
      />
    </div>
  )
}
